import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useErrorToast } from './queryUtils';
import {
  fetchContacts,
  createContact,
  updateContact,
  deleteContact,
  type Contact,
  type ContactInput,
  type ContactListParams,
} from '../services/api/contacts';

/**
 * MKS-344 — reusable sales contacts.
 *
 * Every mutation invalidates `['leads']` and `['lead']` as well as
 * `['contacts']`: a contact is embedded in each lead's payload, so renaming
 * or deleting one changes what the board and the lead modal show.
 */
const CONTACTS_KEY = ['contacts'] as const;

function invalidateContactsAndLeads(queryClient: ReturnType<typeof useQueryClient>): void {
  queryClient.invalidateQueries({ queryKey: CONTACTS_KEY });
  queryClient.invalidateQueries({ queryKey: ['leads'] });
  queryClient.invalidateQueries({ queryKey: ['lead'] });
}

export function useContacts(params: ContactListParams = {}) {
  return useQuery({
    // `customerId` is normalised to null so `undefined` and `null` do not
    // produce two different cache entries for the same "no company filter".
    queryKey: ['contacts', params.search ?? '', params.customerId ?? null],
    queryFn: () => fetchContacts(params),
  });
}

export function useCreateContact() {
  const queryClient = useQueryClient();
  const onError = useErrorToast();
  return useMutation({
    mutationFn: (input: ContactInput) => createContact(input),
    onSuccess: () => invalidateContactsAndLeads(queryClient),
    onError: (err) => onError(err, "Couldn't create contact"),
  });
}

export function useUpdateContact() {
  const queryClient = useQueryClient();
  const onError = useErrorToast();
  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: Partial<ContactInput> }) =>
      updateContact(id, input),
    onSuccess: () => invalidateContactsAndLeads(queryClient),
    onError: (err) => onError(err, "Couldn't update contact"),
  });
}

export function useDeleteContact() {
  const queryClient = useQueryClient();
  const onError = useErrorToast();
  return useMutation({
    mutationFn: (id: number) => deleteContact(id),
    onSuccess: () => invalidateContactsAndLeads(queryClient),
    onError: (err) => onError(err, "Couldn't delete contact"),
  });
}

export type { Contact, ContactInput };
